import PremiumCandles from "./PremiumCandles";
import ThankYouMessageCard from "./ThankYouMessageCard";
import PhotoLightbox from "./PhotoLightbox";
import Image from "next/image";
import dynamic from "next/dynamic";
const Gallery3D = dynamic(() => import("./WallGallery3D"), { ssr: false });
import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useAuth } from "@/hooks/useAuth";

import obituaryService from "@/services/obituary-service";
import { useRouter } from "next/navigation";
import { isValidDate } from "@/utils/dateUtils";
import Modal from "../ui/model";
import { QrModal } from "./QrModal";

/** Same window as “Dogodki” / pogreb: hide after this many days (post-event). */
const EVENT_OR_FUNERAL_VISIBLE_DAYS = 7;
const EVENT_VISIBILITY_MS =
  EVENT_OR_FUNERAL_VISIBLE_DAYS * 24 * 60 * 60 * 1000;

const MemorialPageTopComp = ({
  onOrder,
  set_Id,
  setModal,
  data,
  updateObituary,
  fetchMemory,
  handleFacebookShare,
}) => {
  const { user } = useAuth();
  const router = useRouter();
  // null until we know; the "Dodaj spominsko svečko" link is shown only while the page has no premium candle.
  const [hasPremiumCandle, setHasPremiumCandle] = useState(null);
  // Position of the photo shown in the big view (null = closed).
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [currentURL, setCurrentURL] = useState("");
  const [maxCondolances, setMaxCondolances] = useState(6);
  const [limitedCondolances, setLimitedCondolances] = useState([]);
  const [currentCount, setCurrentCount] = useState(0);
  const [screenWidth, setScreenWidth] = useState(1024);
  const [showQr, setShowQr] = useState(false);
  const [showMemoryIconTooltip, setShowMemoryIconTooltip] = useState(false);
  const [memoryIconTooltipSide, setMemoryIconTooltipSide] = useState("right");
  const memoryIconButtonRef = useRef(null);
  const [showPrivateFuneralIconTooltip, setShowPrivateFuneralIconTooltip] = useState(false);
  const [privateFuneralIconTooltipSide, setPrivateFuneralIconTooltipSide] = useState("right");
  const privateFuneralIconButtonRef = useRef(null);

  useEffect(() => {
    // Set initial screen width on mount (client-side only)
    if (typeof window !== "undefined") {
      setScreenWidth(window.innerWidth);
    }

    const handleResize = () => {
      if (typeof window !== "undefined") {
        setScreenWidth(window.innerWidth);
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
    }

    // Cleanup on unmount
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  const defaultMessage = {
    message: "Počivaj v miru",
    name: "osmrtnica.com",
    relation: "",
    createdTimestamp: data?.createdTimestamp ?? "",
  };

  useEffect(() => {
    if (data && data?.id) {
      setCurrentCount(data?.totalCandles);
    }
  }, [data]);

  // User-scoped key prevents a different user logging in on the same browser
  // from inheriting a previous user's cached cooldown.
  const candleStorageKey = data?.id
    ? user?.id
      ? `candle_${user.id}_${data.id}`
      : `candle_${data.id}`
    : null;

  const combinedDedicationSlides = useMemo(() => {
    const dedications = (data?.Dedications || []).map((d) => ({
      ...d,
      title: d.title || "Posvetilo",
      name: d.name || "",
      message: d.message || "",
      createdTimestamp: d.createdTimestamp,
    }));

    const thankYous = (data?.ThankYouMessages || []).map((t) => ({
      ...t,
      title: t.headline || "Zahvala",
      name: t.authorName || "",
      message: t.message || "",
      createdTimestamp: t.createdTimestamp,
      isThankYou: true,
    }));

    return [...dedications, ...thankYous];
  }, [data?.Dedications, data?.ThankYouMessages]);

  useEffect(() => {
    if (!candleStorageKey) return;
    let stored = null;
    try {
      stored = localStorage.getItem(candleStorageKey);
    } catch {
      // Best-effort lookup only.
    }
    if (!stored) return;
    const serverTime = data?.candles?.myLastBurntCandleTime
      ? new Date(data.candles.myLastBurntCandleTime).getTime()
      : 0;
    if (new Date(stored).getTime() > serverTime) {
      updateObituary({
        ...data,
        candles: { ...data?.candles, myLastBurntCandleTime: stored },
      });
    }
  }, [candleStorageKey]); // eslint-disable-line react-hooks/exhaustive-deps


  const toggleText = () => {
    setShowFullObituaryText((prev) => !prev);
  };

  const openCandleModal = () => {
    setModal(true);
  };

  // The paid popups (the page owns them); without it the old free popup is the fallback.
  const openSpomin = () => {
    if (onOrder) onOrder("memory");
    else {
      set_Id("13");
      openCandleModal();
    }
  };
  const openPremiumCandleOrder = () => onOrder?.("candle");

  const closeCandleModal = () => {
    setModal(false);
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const decodedURL = decodeURIComponent(window.location.href);
      setCurrentURL(decodedURL);
    }
  }, []);

  const parsedEvents = data?.events ? data?.events : [];

  // Extract refuseFlowersIcon from database (stored in obituary record)
  const refuseFlowersIcon = data?.refuseFlowersIcon === true || data?.refuseFlowersIcon === 1;

  // Extract privateFuneralIcon from database (stored in obituary record)
  const privateFuneralIcon = data?.privateFuneralIcon === true || data?.privateFuneralIcon === 1;

  const handleCopyURL = () => {
    navigator.clipboard
      .writeText(currentURL) // Copy to clipboard
      .then(() => {
        toast.success("Kopirano"); // Show success toast
      })
      .catch(() => {
        toast.error("Kopiranje ni uspelo"); // Show error toast in case of failure
      });
  };

  const truncateURL = (url, maxLength) => {
    return url.length > maxLength ? `${url.slice(0, maxLength)}...` : url;
  };
  const formatObituaryDate = (dateString) => {
    if (!dateString) return "";

    // Parse date - handle ISO format (yyyy-mm-dd) and other formats
    let date;
    if (typeof dateString === 'string' && dateString.includes('T')) {
      // ISO format with time
      date = new Date(dateString);
    } else if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
      // ISO format date only (yyyy-mm-dd)
      date = new Date(dateString + 'T00:00:00');
    } else {
      date = new Date(dateString);
    }

    // Validate the date
    if (isNaN(date.getTime())) {
      return "";
    }

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    // Format as dd.MM.yyyy (European format with dots)
    const dayStr = String(day).padStart(2, '0');
    const monthStr = String(month).padStart(2, '0');
    return `${dayStr}.${monthStr}.${year}`;
  };

  const formatDateForDatetime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    // Return full ISO date
    return format(date, "yyyy-MM-dd");
  };

  const formattedBirthDate = formatObituaryDate(data?.birthDate);
  const formattedDeathDate = formatObituaryDate(data?.deathDate);

  const getDeathYear = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.getFullYear().toString();
  };
  const deathYear = (isValidDate(data?.deathDate) ? getDeathYear(data?.deathDate) : "") || (data?.deathYear != null ? String(data.deathYear) : "");
  const birthDisplayText = (data?.birthDate && !data?.birthDate?.includes?.("1025") && isValidDate(data?.birthDate))
    ? formattedBirthDate
    : (data?.birthYear != null ? String(data.birthYear) : "");
  const deathDisplayText = isValidDate(data?.deathDate)
    ? formattedDeathDate
    : (data?.deathYear != null ? String(data.deathYear) : "");

  const formatTitleCase = (str) => {
    return str;
  };
  const burnCandle = async () => {
    // if (!user) {
    //   toast.error("Please sign in to light a candle");
    //   return;
    // }

    const candleData = { userId: user ? user.id : null };

    try {
      const response = await obituaryService.burnCandle(data?.id, candleData);

      if (response?.candle?.id) {
        toast.success("Svečka je prižgana");
        openCandleModal();
        set_Id("3");

        const burnedAt = response.candle?.createdAt || new Date().toISOString();
        try {
          if (candleStorageKey) localStorage.setItem(candleStorageKey, burnedAt);
        } catch {
          // Best-effort persistence only.
        }

        const updatedCandles = {
          ...data?.candles,
          totalCandles: (data?.candles?.totalCandles || 0) + 1,
          lastBurnedCandleTime: burnedAt,
          myLastBurntCandleTime: burnedAt,
        };

        updateObituary({
          ...data,
          candles: updatedCandles,
        });
      }
    } catch (error) {
      console.error("Failed to burn candle:", error);

      if (error?.status === 409 || error?.response?.status === 409) {
        let stored = null;
        try {
          if (candleStorageKey) stored = localStorage.getItem(candleStorageKey);
        } catch {
          // Best-effort lookup only.
        }
        if (stored) {
          updateObituary({
            ...data,
            candles: { ...data?.candles, myLastBurntCandleTime: stored },
          });
        }
        openCandleModal();
        set_Id("3");
        toast.error("Vsakih 24 ur lahko prižgeš eno svečko");
      } else {
        // toast.error(
        //   error.data?.message || "Error burning candle. Please try again."
        // );
      }
    }
  };

  //obituary text setting
  const [showFullObituaryText, setShowFullObituaryText] = useState(false);
  const [isPopUP1, setIsPopUP1] = useState(false);
  const [isPopUP2, setIsPopUP2] = useState(false);

  const toggleObituaryText = () => {
    setShowFullObituaryText((prev) => !prev);
  };

  const characterLimit = 300;
  const obituary = data?.obituary || "";
  const shouldTruncate = obituary.length > characterLimit;

  const displayedObituary =
    showFullObituaryText || !shouldTruncate
      ? obituary
      : obituary.slice(0, characterLimit);
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("sl-SI", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (data?.Condolences && data?.Condolences?.length > 0) {
      // Newest first, whatever order the list came in (a sympathy added a moment ago is on top too).
      const newestFirst = [...data.Condolences].sort(
        (a, b) => new Date(b.createdTimestamp) - new Date(a.createdTimestamp)
      );
      setLimitedCondolances(newestFirst.slice(0, maxCondolances));
    }
  }, [data, data?.Condolances, maxCondolances]);

  async function handleQr() {
    if (!data?.qr_code) {
      const response = await obituaryService.generateQR({
        id: data.id,
        slugKey: data.slugKey,
      });

      if (response?.success) {
        data.qr_code = response.qr_code;
        setShowQr(response.qr_code);
      }
    } else if (data?.id && data?.qr_code) {
      setShowQr(data?.qr_code);
    }
  }

  return (
    <div
      id="memoryPageTop"
      className="flex flex-col w-full items-center  justify-center"
    >
      {showQr && (
        <QrModal isShowModal={showQr} setIsShowModal={setShowQr} data={data} />
      )}
      <div
        className="bg-[#ecf0f3]   w-full flex justify-center 
      mt-[53px] tablet:mt-[60px]   pt-[60px] relative"
      >
        <Image
          src={"/memory_page_bg.png"}
          alt="Ozadje spominske strani"
          width={1280}
          height={1091}
          className="absolute top-0 left-0 w-full h-[120%] pointer-events-none -z-10"
        />
        <div
          className="flex flex-col 
               w-[100%] px-[14px] tablet:px-0 desktop:px-0
               tablet:w-[525px]
               desktop:w-[1089px]  
               desktop:h-[auto]    
               "
        >
          <div className="flex  flex-col desktop:flex-row w-[100%] relative">
            <div className="flex  w-[100%] justify-center desktop:w-[50%] static">
              <div className="flex  flex-col w-[100%]">
                <div className="flex  flex-col w-[100%]  items-center  desktop:sticky top-[115px]">
                  <div className="hidden desktop:flex h-[30px]" />
                  <div className=" ">
                    <div
                      className="text-[64px] text-[#414141] font-greatVibes font-normal text"
                      style={{
                        textShadow:
                          "0px 1px 1px #000000, 0px 4px 4px #00000040",
                      }}
                    >
                      V spomin
                    </div>
                  </div>
                  <div
                    className="mt-[40px] flex items-center justify-center overflow-hidden rounded-[8px]"
                    style={{
                      boxShadow:
                        "-5px -5px 10px 0px #FFFFFF80, 5px 5px 10px 0px #C2C2C280, -1px -1px 2px 0px #FFFFFF",
                    }}
                  >
                    <div
                      className="bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF]"
                      style={{
                        border: "4px solid",
                        borderImageSource:
                          "linear-gradient(113.63deg, #E3E8EC 0%, #FFFFFF 100%)",
                        borderImageSlice: 1,
                      }}
                    >
                      <Image
                        src={data?.image || "/user5.jpeg"}
                        alt={data?.name && data?.sirName ? `Fotografija ${data.name} ${data.sirName} - Osmrtnica.com` : "Fotografija pokojnika"}
                        width={1000}
                        height={1000}
                        className="h-[266.87px] w-[195px] 
                                   tablet:h-[266.87px] tablet:w-[195px] 
                                   desktop:h-[266.87px]  desktop:w-[195px] 
                                   rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col justify-center mt-[50px]">
                    <div className="flex items-center justify-center h-[33px] tablet:h-[47px] desktop:h-[40px]">
                      {/* <h1 className="text-[#1E2125] text-[28px] tablet:text-[40px] desktop:text-[40px] font-variation-customOpt28 tablet:font-variation-customOpt40 desktop:font-variation-customOpt40 font-normal">
                        {data?.name && data?.sirName
                          ? `${formatTitleCase(data?.name)} ${formatTitleCase(
                            data?.sirName
                          )}`.length > 25
                            ? `${`${formatTitleCase(
                              data?.name
                            )} ${formatTitleCase(data?.sirName)}`.slice(
                              0,
                              25
                            )}...`
                            : `${formatTitleCase(data?.name)} ${formatTitleCase(
                              data?.sirName
                            )}`
                          : ""}
                      </h1> */}
                      {data?.name && data?.sirName && (
                        <h1 className="text-[#1E2125] text-[28px] tablet:text-[40px] desktop:text-[40px] font-variation-customOpt28 tablet:font-variation-customOpt40 desktop:font-variation-customOpt40 font-normal">
                          {(() => {
                            const fullName = `${formatTitleCase(
                              data?.name
                            )} ${formatTitleCase(data?.sirName)}`;
                            return fullName.length > 25
                              ? `${fullName.slice(0, 25)}...`
                              : fullName;
                          })()}
                        </h1>
                      )}
                    </div>
                    <p className="flex items-center justify-center mt-[14px] h-[21px] tablet:h-[23px] desktop:h-[20px] text-[#1E2125] text-[18px] tablet:text-[20px] desktop:text-[20px] font-variation-customOpt18 tablet:font-variation-customOpt20 desktop:font-variation-customOpt20 font-normal">
                      {(() => {
                        if (birthDisplayText) {
                          const dateRangeText = deathDisplayText
                            ? `${birthDisplayText} - ${deathDisplayText}`
                            : birthDisplayText;
                          return deathDisplayText && (isValidDate(data?.deathDate) || data?.deathYear != null) ? (
                            <time dateTime={isValidDate(data?.deathDate) ? formatDateForDatetime(data.deathDate) : (data?.deathYear ?? "")}>{dateRangeText}</time>
                          ) : (
                            <>{dateRangeText}</>
                          );
                        }
                        if (data?.ageInYears != null && data?.ageInYears > 0) {
                          return (isValidDate(data?.deathDate) || data?.deathYear != null) ? (
                            <time dateTime={isValidDate(data?.deathDate) ? formatDateForDatetime(data.deathDate) : (data?.deathYear ?? "")}>{deathDisplayText} (v {data.ageInYears}. letu)</time>
                          ) : (
                            <>{deathDisplayText} (v {data.ageInYears}. letu)</>
                          );
                        }
                        return (isValidDate(data?.deathDate) || data?.deathYear != null) ? (
                          <time dateTime={isValidDate(data?.deathDate) ? formatDateForDatetime(data.deathDate) : (data?.deathYear ?? "")}>{deathDisplayText}</time>
                        ) : (
                          <>{deathDisplayText}</>
                        );
                      })()}
                    </p>
                    <p className="flex items-center justify-center mt-[14px] h-[21px] tablet:h-[23px] desktop:h-[20px] text-[#414141] text-[18px] tablet:text-[20] desktop:text-[20px] font-variation-customOpt18 tablet:font-variation-customOpt20 desktop:font-variation-customOpt20 font-normal">
                      {data?.location ? formatTitleCase(data?.location) : ""}
                    </p>
                    <div className="flex flex-col w-[100%] mt-[40px] tablet:[31px] desktop:mt-[30px] h-auto ">
                      <p className="text-[24px] text-[#414141] font-normal font-greatVibes text-center mb-[72px]">
                        {data?.verse
                          ? data?.verse
                          : "The song is ended but the melody lingers on."}
                      </p>

                      {/* <p className="text-[16px] text-[#414141] font-normal font-greatVibes text-center desktop:text-end">
                        Irving Berlin
                      </p> */}
                    </div>
                  </div>
                </div>
                {data && data?.symbol && (
                  <>
                    <div
                      className="hidden desktop:flex tablet:flex absolute z-10  items-end w-[100%]  
                                          pl-[62%] tablet:pl-0 desktop:pl-0 tablet:w-[525px]  desktop:w-[545px] "
                    >
                      <div
                        className=" flex self-center mt-[50px]  desktop:mt-[160px]
                                              tablet:w-[90%] tablet:justify-end tablet:mt-[108px] desktop:w-[540px] desktop:pt-[0px] desktop:pl-[389px]
                                        "
                      >
                        {data?.symbol === "1" && (
                          <Image
                            src={"/icon_cross.png"}
                            alt="Verski simbol - križ"
                            width={1000}
                            height={1000}
                            className="h-[94.29px] w-[73.19px] tablet:h-[116px] tablet:w-[89px] desktop:h-[105px] desktop:w-[89px] "
                          />
                        )}
                        {data?.symbol === "2" && (
                          <Image
                            src={"/img_plus2.png"}
                            alt="Verski simbol"
                            width={1000}
                            height={1000}
                            className="h-[94.29px] w-[73.19px] tablet:h-[116px] tablet:w-[89px] desktop:h-[105px] desktop:w-[89px] "
                          />
                        )}
                        {data?.symbol === "3" && (
                          <Image
                            src={"/img_moon_star.png"}
                            alt="Verski simbol - luna in zvezda"
                            width={1000}
                            height={1000}
                            className="h-[94.29px] w-[73.19px] tablet:h-[95px] tablet:w-[89px] desktop:h-[85px] desktop:w-[89px] "
                          />
                        )}
                        {data?.symbol === "4" && (
                          <Image
                            src={"/img_plus3.png"}
                            alt="Verski simbol"
                            width={1000}
                            height={1000}
                            className="h-[94.29px] w-[73.19px] tablet:h-[116px] tablet:w-[89px] desktop:h-[105px] desktop:w-[89px] "
                          />
                        )}
                        {data?.symbol === "5" && (
                          <Image
                            src={"/img_star.png"}
                            alt="Verski simbol - zvezda"
                            width={1000}
                            height={1000}
                            className="h-[94.29px] w-[73.19px] tablet:h-[116px] tablet:w-[89px] desktop:h-[105px] desktop:w-[89px] "
                          />
                        )}
                      </div>
                    </div>
                    <div className="flex tablet:hidden desktop:hidden absolute z-10 self-center  justify-end w-[330px] ">
                      <div className=" flex self-center mt-[86px] mr-[10px] ">
                        {data?.symbol === "1" && (
                          <Image
                            src={"/icon_cross.png"}
                            alt="Verski simbol - križ"
                            width={1000}
                            height={1000}
                            className="h-[80.29px] w-[60.19px] "
                          />
                        )}
                        {data?.symbol === "2" && (
                          <Image
                            src={"/img_plus2.png"}
                            alt="Verski simbol"
                            width={1000}
                            height={1000}
                            className="h-[80.29px] w-[60.19px] "
                          />
                        )}
                        {data?.symbol === "3" && (
                          <Image
                            src={"/img_moon_star.png"}
                            alt="Verski simbol - luna in zvezda"
                            width={1000}
                            height={1000}
                            className="h-[65.29px] w-[60.19px] "
                          />
                        )}
                        {data?.symbol === "4" && (
                          <Image
                            src={"/img_plus3.png"}
                            alt="Verski simbol"
                            width={1000}
                            height={1000}
                            className="h-[80.29px] w-[60.19px] "
                          />
                        )}

                        {data?.symbol === "5" && (
                          <Image
                            src={"/img_star.png"}
                            alt="Verski simbol - zvezda"
                            width={1000}
                            height={1000}
                            className="h-[80.29px] w-[60.19px] "
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex w-[100%]    desktop:mt-0 desktop:w-[50%]   ">
              <div className="flex flex-col w-[100%]   desktop:items-end items-center">
                <div className="hidden desktop:flex h-[35px] w-full" />
                <div
                  className={`flex-col 
                  pt-4 w-[100%] mobile:px-[21px] mobile:pb-[19px]
                  tablet:px-[22px] tablet:pb-[15px]
                  ${screenWidth < 740
                      ? "w-[330px] mobile:!w-[100%] mobile:!max-w-[470px]"
                      : screenWidth >= 740 && screenWidth <= 1024
                        ? "w-[550px]"
                        : ""
                    }
                  desktop:w-[517px] desktop:pl-[22px] desktop:pr-[17px] bg-gradient-to-br rounded-2xl from-[#E3E8EC] to-[#FFFFFF]`}
                  style={{
                    background:
                      "linear-gradient(113.63deg, #E3E8EC 0%, #FFFFFF 100%)",
                    boxShadow:
                      "-5px -5px 10px 0px #FFFFFF, 5px 5px 10px 0px #C2C2C280",
                  }}
                >
                  <div className="flex items-center h-[39px] ">
                    <h2 className="text-[20px] text-[#1E2125] font-variation-customOpt20 font-normal">
                      Osmrtnica
                    </h2>
                  </div>
                  <div className="bg-[#D4D4D4] h-[1px] w-[100%] my-4 " />
                  {/* <div className="flex h-[79px] desktop:h-[71px] bg-yellow-400"> */}
                  <div className="flex desktop:pb-[20px] p-[14px]    ">
                    <p className="text-[#414141] text-[16px] font-variation-customOpt16 font-normal leading-6 hidden desktop:block tablet:block">
                      {obituary.split("\n").map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </p>
                    <p className="text-[#414141] text-[16px] font-variation-customOpt16 font-normal leading-6 hidden mobile:block ">
                      {displayedObituary.split("\n").map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                  <div className="  justify-end hidden mobile:flex">
                    {shouldTruncate && (
                      <button
                        onClick={toggleObituaryText}
                        className="text-blue-500 ml-auto"
                      >
                        {showFullObituaryText ? "Zapri" : "Odpri več"}
                      </button>
                    )}
                  </div>
                </div>

                <div
                  onClick={() => {
                    set_Id("14");
                    openCandleModal();
                  }}
                  className="flex cursor-pointer self-end mt-[20px] pr-[7px] tablet:pr-[9px] tablet:mt-[12px] desktop:mt-[14px] h-[14px] tablet:h-[16px] desktop:h-[16px] items-center desktop:pr-[20px] tablet:mb-[26px] mobile:mb-[26px]"
                >
                  {/* {user && data?.User && user.id === data?.User.id && (
                    <> */}
                  <Image
                    src={"/pan.png"}
                    alt="Ikona"
                    width={12}
                    height={12}
                    className=""
                  />
                  <p className="text-[12px] text-[#414141] font-variation-customOpt12 font-normal ml-[10px]">
                    Spremeni / dopolni podatke na strani
                  </p>
                  {/* </>
                  )} */}
                </div>

                {data &&
                  (data?.funeralTimestamp ||
                    (parsedEvents && parsedEvents.length > 0)) &&
                  [
                    ...(data?.funeralTimestamp
                      ? [
                        {
                          type: "funeral",
                          timestamp: new Date(
                            data?.funeralTimestamp
                          ).getTime(),
                          details: data,
                        },
                      ]
                      : []),
                    ...(Array.isArray(parsedEvents) ? parsedEvents : [])
                      .filter((event) => {
                        const eventDate = new Date(event.eventDate).getTime();
                        const today = new Date().getTime();
                        return eventDate + EVENT_VISIBILITY_MS >= today;
                      })

                      .map((event) => ({
                        type: "event",
                        timestamp: new Date(event.eventDate).setHours(
                          event.eventHour || 0,
                          event.eventMinute || 0
                        ), // Include eventHour and eventMinute in the timestamp
                        details: event,
                      })),
                  ]
                    .filter((item) => {
                      const today = new Date().getTime();
                      return item.timestamp + EVENT_VISIBILITY_MS >= today;
                    })
                    .sort((a, b) => a.timestamp - b.timestamp).length > 0 && (
                    <div
                      className={`flex-col w-[100%] pt-4 
                      mobile:px-[21px]  mobile:pb-[25px] 
                      tablet:pb-[23px]  tablet:px-[22px]                          
                      desktop:w-[517px] ${screenWidth < 740
                          ? "w-[330px] mobile:!w-[100%] mobile:!max-w-[470px]"
                          : screenWidth >= 740 && screenWidth <= 1024
                            ? "w-[550px]"
                            : ""
                        }  desktop:pb-[14px] desktop:pl-[22px] desktop:pr-[17px] shadow-custom-light-dark-box bg-gradient-to-br rounded-2xl from-[#E3E8EC] to-[#FFFFFF] mb-[28px]`}
                      style={{
                        background:
                          "linear-gradient(113.63deg, #E3E8EC 0%, #FFFFFF 100%)",
                        boxShadow:
                          "-5px -5px 10px 0px #FFFFFF, 5px 5px 10px 0px #C2C2C280",
                      }}
                    >
                      <div className="flex  h-[38px] items-center">
                        <h2 className="text-[20px] text-[#1E2125] font-variation-customOpt20 font-normal  ">
                          Dogodki
                        </h2>
                      </div>
                      <div className="bg-[#D4D4D4] h-[1px] w-[100%] my-4 " />
                      {[
                        ...(data?.funeralTimestamp
                          ? [
                            {
                              type: "funeral",
                              timestamp: new Date(
                                data?.funeralTimestamp
                              ).getTime(),
                              details: data,
                            },
                          ]
                          : []),
                        ...(Array.isArray(parsedEvents) ? parsedEvents : [])
                          .filter((event) => {
                            const eventDate = new Date(
                              event.eventDate
                            ).getTime();
                            const today = new Date().getTime();
                            return eventDate + EVENT_VISIBILITY_MS >= today;
                          })

                          .map((event) => ({
                            type: "event",
                            timestamp: new Date(event.eventDate).setHours(
                              event.eventHour || 0,
                              event.eventMinute || 0
                            ), // Include eventHour and eventMinute in the timestamp
                            details: event,
                          })),
                      ]
                        .filter((item) => {
                          const today = new Date().getTime();
                          return (
                            item.timestamp + EVENT_VISIBILITY_MS >= today
                          );
                        })
                        .sort((a, b) => a.timestamp - b.timestamp) // Sort by earliest to latest
                        .map((item, index) => {
                          const date = new Date(item.timestamp);
                          const day = date.getDate();
                          const month = date
                            .toLocaleString("sl-SI", { month: "short" })
                            .replace(/\.$/, "");
                          const weekday = date
                            .toLocaleDateString("sl-SI", { weekday: "long" })
                            .replace(/^\S/, (c) => c.toLocaleUpperCase("sl"));
                          const time = `${date
                            .getHours()
                            .toString()
                            .padStart(2, "0")}:${date
                              .getMinutes()
                              .toString()
                              .padStart(2, "0")}`;

                          if (item.type === "funeral") {
                            return (
                              <div
                                key={`funeral-${index}`}
                                className="flex mobile:py-[15px] flex-row tablet:mr-0 desktop:mt-0 tablet:py-4 tablet:pl-[15.88px] tablet:pr-[60px] desktop:py-4 desktop:pl-[15.88px]"
                              >
                                <div className="w-[58px] tablet:w-[68px] desktop:w-[80px]">
                                  <div className="flex flex-col bg-[#144D82] h-[54px] w-[55px] tablet:h-[64px] tablet:w-[64px] desktop:h-[64px] desktop:w-[64px] rounded-lg justify-center items-center">
                                    <div className="text-[#FFFFFF] text-[16px] tablet:text-[24px] desktop:text-[24px] font-variation-customOpt24wght900 font-[900]">
                                      {day}
                                    </div>
                                    <div className="text-[#FFFFFF] text-[12px] tablet:text-[14px] desktop:text-[12px] font-normal">
                                      {month}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col pl-[15px]">
                                  <div className="flex items-center h-[23px] gap-2">
                                    <div className="text-[#1E2125] text-[20px] font-medium">
                                      Pogreb
                                    </div>
                                    {privateFuneralIcon && (
                                      <div
                                        className="cursor-pointer flex items-center justify-center relative"
                                        onClick={() => {
                                          if (privateFuneralIconButtonRef.current) {
                                            const rect = privateFuneralIconButtonRef.current.getBoundingClientRect();
                                            const windowWidth = window.innerWidth;
                                            if (rect.right + 260 > windowWidth) {
                                              setPrivateFuneralIconTooltipSide("left");
                                            } else {
                                              setPrivateFuneralIconTooltipSide("right");
                                            }
                                          }
                                          setShowPrivateFuneralIconTooltip(true);
                                          // Auto-hide after 3 seconds
                                          setTimeout(() => {
                                            setShowPrivateFuneralIconTooltip(false);
                                          }, 3000);
                                        }}
                                        ref={privateFuneralIconButtonRef}
                                      >
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="fill-[#0A85C2]"
                                        >
                                          <path d="M20.825 12c-.228.59-3.052 6.998-3.772 8.614-.756 1.695-2.229 3.386-4.951 3.386h-4.729c-2.865 0-4.373-1.7-4.373-4.673v-12.975c0-1.721 2.37-1.633 2.37-.08v5.689c0 .468.858.465.858 0v-9.142c0-1.769 2.65-1.722 2.65 0v8.63c0 .476.797.456.797-.01v-10.128c0-1.722 2.624-1.773 2.624 0l.001 10.245c0 .459.826.469.826 0v-8.604c0-1.629 2.873-1.679 2.873 0v9.75c0 .597.587.692.811.236.212-.433 1.089-2.368 1.1-2.389.883-1.849 3.832-.726 2.915 1.451z" />
                                        </svg>
                                        {showPrivateFuneralIconTooltip && (
                                          <div
                                            className={`absolute top-1/2 -translate-y-1/2 z-20 w-max max-w-[260px] rounded-md bg-[#0A85C2] text-white text-[12px] leading-[16px] px-3 py-2 shadow-lg after:content-[''] after:absolute after:top-1/2 after:-translate-y-1/2 ${privateFuneralIconTooltipSide === "right"
                                                ? "left-full ml-3 after:left-[-6px] after:border-y-[6px] after:border-y-transparent after:border-r-[6px] after:border-r-[#0A85C2]"
                                                : "right-full mr-3 after:right-[-6px] after:border-y-[6px] after:border-y-transparent after:border-l-[6px] after:border-l-[#0A85C2]"
                                              }`}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            Pogreb v družinskem krogu
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {refuseFlowersIcon && (
                                      <div
                                        className="cursor-pointer flex items-center justify-center relative"
                                        onClick={() => {
                                          if (memoryIconButtonRef.current) {
                                            const rect = memoryIconButtonRef.current.getBoundingClientRect();
                                            const windowWidth = window.innerWidth;
                                            if (rect.right + 260 > windowWidth) {
                                              setMemoryIconTooltipSide("left");
                                            } else {
                                              setMemoryIconTooltipSide("right");
                                            }
                                          }
                                          setShowMemoryIconTooltip(true);
                                          // Auto-hide after 3 seconds
                                          setTimeout(() => {
                                            setShowMemoryIconTooltip(false);
                                          }, 3000);
                                        }}
                                        ref={memoryIconButtonRef}
                                      >
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fillRule="evenodd"
                                          clipRule="evenodd"
                                          className="fill-[#0A85C2]"
                                        >
                                          <path d="M1 15c4.075-1.121 9.51.505 11 6 1.985-5.939 7.953-7.051 11-6-2.467 1.524-3.497 9-11 9s-8.487-7.471-11-9zm8.203-12.081c.008-1.612 1.319-2.919 2.933-2.919 1.615 0 2.926 1.307 2.934 2.919 1.4-.799 3.187-.317 3.995 1.081.807 1.398.331 3.187-1.062 4 1.393.813 1.869 2.602 1.062 4-.808 1.398-2.595 1.88-3.995 1.081-.008 1.612-1.319 2.919-2.934 2.919-1.614 0-2.925-1.307-2.933-2.919-1.4.799-3.188.317-3.995-1.081-.807-1.398-.331-3.187 1.062-4-1.393-.813-1.869-2.602-1.062-4 .807-1.398 2.595-1.88 3.995-1.081zm2.797 2.581c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5z" />
                                          <line
                                            x1="21"
                                            y1="3"
                                            x2="3"
                                            y2="21"
                                            stroke="#1E2125"
                                            strokeWidth="2.3"
                                            strokeLinecap="round"
                                          />
                                        </svg>
                                        {showMemoryIconTooltip && (
                                          <div
                                            className={`absolute top-1/2 -translate-y-1/2 z-20 w-max max-w-[260px] rounded-md bg-[#0A85C2] text-white text-[12px] leading-[16px] px-3 py-2 shadow-lg after:content-[''] after:absolute after:top-1/2 after:-translate-y-1/2 ${memoryIconTooltipSide === "right"
                                                ? "left-full ml-3 after:left-[-6px] after:border-y-[6px] after:border-y-transparent after:border-r-[6px] after:border-r-[#0A85C2]"
                                                : "right-full mr-3 after:right-[-6px] after:border-y-[6px] after:border-y-transparent after:border-l-[6px] after:border-l-[#0A85C2]"
                                              }`}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            Svojci cvetje in sveče hvaležno odklanjajo.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex mt-[8px] flex-col">
                                    <p className="text-[#414141] text-[14px] font-normal leading-[16.41px]">
                                      {weekday}, {time}
                                    </p>
                                    <p className="text-[#414141] text-[14px] font-normal leading-[16.41px]">
                                      {(() => {
                                        // Check new Cemeteries relation first, then fallback to old Cemetry, then default
                                        const cemeteryName = data?.Cemeteries?.name || data?.Cemetry?.name || "Pokopalisce";

                                        return cemeteryName.length > 50
                                          ? `${cemeteryName.slice(0, 50)}...`
                                          : cemeteryName;
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <div
                                key={`event-${index}`}
                                className="flex mobile:py-[15px] flex-row tablet:mr-0 desktop:mt-0 tablet:py-4 tablet:pl-[15.88px] tablet:pr-[60px] desktop:py-4 desktop:pl-[15.88px]"
                              >
                                <div className="w-[58px] tablet:w-[68px] desktop:w-[80px]">
                                  <div className="flex flex-col bg-[#144D82] h-[54px] w-[55px] tablet:h-[64px] tablet:w-[64px] desktop:h-[64px] desktop:w-[64px] rounded-lg justify-center items-center">
                                    <div className="text-[#FFFFFF] text-[16px] tablet:text-[24px] desktop:text-[24px] font-variation-customOpt24wght900 font-[900]">
                                      {day}
                                    </div>
                                    <div className="text-[#FFFFFF] text-[12px] tablet:text-[14px] desktop:text-[12px] font-normal">
                                      {month}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col pl-[15px]">
                                  <div className="flex items-center h-[23px]">
                                    <div className="text-[#1E2125] text-[20px] font-medium">
                                      {item.details.eventName
                                        ? formatTitleCase(
                                          item.details.eventName
                                        )
                                        : ""}
                                    </div>
                                  </div>
                                  <div className="flex mt-[8px] flex-col">
                                    <p className="text-[#414141] text-[14px] font-normal leading-[16.41px]">
                                      {weekday}, {time}
                                    </p>
                                    <p className="text-[#414141] text-[14px] font-normal leading-[16.41px]">
                                      {item.details.eventLocation
                                        ? item.details.eventLocation.length > 50
                                          ? `${item.details.eventLocation.slice(
                                            0,
                                            50
                                          )}...`
                                          : item.details.eventLocation
                                        : ""}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        })}
                    </div>
                  )}

                {false && (
                  <div className=" flex tablet:hidden desktop:hidden my-[14px] self-end items-center pr-[7px]">
                    <Image
                      src={"/placeholder_icon.png"}
                      alt="Ikona označevalca"
                      width={18}
                      height={18}
                      className=""
                    />
                    <p className="text-[12px] text-[#414141] font-variation-customOpt12 font-normal ml-[10px] ">
                      Admin te strani:
                    </p>
                    <p className="text-[12px] text-[#0A85C2] font-variation-customOpt12 font-normal ml-[5px] ">
                      Mike Jones, Kim Jones
                    </p>
                  </div>
                )}

                <div
                  className={`
                  flex-col pt-4 pl-[22px] pr-[18px] w-[100%]                       
                  desktop:w-[517px] ${screenWidth < 740
                      ? "w-[330px] mobile:!w-[100%] mobile:!max-w-[470px]"
                      : screenWidth >= 740 && screenWidth <= 1024
                        ? "w-[550px]"
                        : ""
                    } desktop:pl-[22px] desktop:pr-[14px]
                  bg-gradient-to-br rounded-2xl from-[#E3E8EC] to-[#FFFFFF]
                  ${parsedEvents.length === 90
                      ? "desktop:mt-2"
                      : "desktop:mt-[24px]"
                    }
                  `}
                  style={{
                    background:
                      "linear-gradient(113.63deg, #E3E8EC 0%, #FFFFFF 100%)",
                    boxShadow:
                      "-5px -5px 10px 0px #FFFFFF, 5px 5px 10px 0px #C2C2C280",
                  }}
                >
                  <div
                    className="flex flex-col tablet:flex-row desktop:flex-row tablet:justify-between 
                  desktop:justify-between h-[39px] "
                  >
                    <div className="text-[20px] text-[#1E2125] font-variation-customOpt20 font-normal desktop:mt-[2px] ">
                      Prižgi svečko v spomin
                    </div>
                    {/* <div className="hidden  tablet:flex desktop:flex text-[12px] text-[#1E2125] font-normal mt-[10px]">
                      Skupno ta teden: {data?.currentWeekVisits}
                    </div> */}
                    {/* <div className="flex tablet:hidden desktop:hidden self-end text-[12px] text-[#1E2125] font-normal mt-[-10px] ">
                      Skupno ta teden: {data?.currentWeekVisits}
                    </div> */}
                  </div>
                  <div className="bg-[#D4D4D4] h-[1px]  w-[100%] mt-4 " />
                  <div className="flex  mt-[18px] tablet:mt-[15px] mb-[25px] desktop:mt-[15px] justify-between pl-0 tablet:pl-[10px] desktop:pl-[15px]">
                    <div
                      onClick={() => {
                        burnCandle();
                      }}
                      className="flex cursor-pointer h-[62px] w-[62px] items-center justify-center border-[#B9DFF2] rounded-full"
                      style={{
                        background:
                          "linear-gradient(113.63deg, #E3E8EC 0%, #FFFFFF 100%)",
                        boxShadow:
                          "-5px -5px 10px 0px #FFFFFF, 5px 5px 10px 0px #C2C2C280",
                      }}
                    >
                      <Image
                        src={"/candle.png"}
                        alt="Ikona svečke"
                        width={36}
                        height={44}
                        className=""
                      />
                    </div>
                    <div className="flex flex-col mt-[5px]  tablet:mt-0 desktop:mt-0">
                      <div className="flex h-6 tablet:h-[40px] desktop:h-[36px] justify-end">
                        <p className="text-[#1E2125] text-[16px] font-variation-customOpt16 font-normal ">
                          Skupno svečk: {data?.totalCandles}
                        </p>
                      </div>
                      {/* <div className="flex h-[29px] tablet:h-[40]  desktop:h-[40px]">
                        <p className="text-[#1E2125] text-[16px] font-variation-customOpt16 font-normal ">
                          Skupno obiskov: {data?.totalVisits}
                        </p>
                      </div> */}
                    </div>
                  </div>
                </div>

                <div
                  className={`flex self-end ${parsedEvents.length === 90
                      ? "tablet:mt-2 desktop:mt-[10px] mobile:mt-[10px]"
                      : "tablet:mt-4 desktop:mt-[28px] mobile:mt-[28px]"
                    } desktop:h-[0px] items-center desktop:pr-[20px]`}
                >
                  {false && (
                    <>
                      <Image
                        src={"/placeholder_icon.png"}
                        alt="Ozadje spominske strani"
                        width={18}
                        height={18}
                        className=""
                      />
                      <p className="text-[12px] text-[#414141] font-variation-customOpt12 font-normal ml-[10px] ">
                        Admin te spominske strani:
                      </p>
                      <p className="text-[12px] text-[#0A85C2] font-variation-customOpt12 font-normal ml-[5px] ">
                        Mike Jones, Kim Jones
                      </p>
                    </>
                  )}
                </div>

                <div
                  className={`flex-col  tablet:mt-0 
                            desktop:mt-0
                            py-4      
                            pl-[21px] pr-[28px]
                            w-[100%] tablet:px-4 
                            desktop:w-[517px] ${screenWidth < 740
                      ? "w-[330px] mobile:!w-[100%] mobile:!max-w-[470px]"
                      : screenWidth >= 740 && screenWidth <= 1024
                        ? "w-[550px]"
                        : ""
                    } desktop:pl-[22px] desktop:pr-[17px] shadow-custom-light-dark-box bg-gradient-to-br rounded-2xl from-[#E3E8EC] to-[#FFFFFF]`}
                  style={{
                    background:
                      "linear-gradient(113.63deg, #E3E8EC 0%, #FFFFFF 100%)",
                    boxShadow:
                      "-5px -5px 10px 0px #FFFFFF, 5px 5px 10px 0px #C2C2C280",
                  }}
                >
                  <div className="flex items-center  h-[39px]">
                    <div className="text-[20px] text-[#1E2125] font-variation-customOpt20 font-normal  ">
                      Povabi družino in prijatelje
                    </div>
                  </div>
                  <div className="bg-[#D4D4D4] h-[1px] w-[100%] my-4 " />
                  <div
                    className="flex flex-col items-center
                                tablet:flex-row tablet:justify-between  tablet:px-2  tablet:py-[14px]                            
                                desktop:flex-row desktop:justify-between mb-2  desktop:pr-[7px] "
                  >
                    <div
                      onClick={() => {
                        set_Id("22");
                        openCandleModal();
                      }}
                      className="flex cursor-pointer flex-row items-center h-[50px] w-[209px] justify-center bg-[#f8ecda] border-[2px] border-[#d9a800] rounded-lg "
                    >
                      <Image
                        src={"/message_icon.png"}
                        alt="Ikona sporočila"
                        width={17}
                        height={15}
                        className=""
                      />
                      <div className="text-[16px] cursor-pointer text-[#d9a800] ml-[10px] font-variation-customOpt16 font-normal">
                        Obvesti jih
                      </div>
                    </div>
                    <div className="flex mt-[20px] tablet:mt-0 desktop:mt-0 flex-row items-center h-[50px] w-[209px] justify-center bg-[#DAF3F8] border-[#00B4D8] border-[2px] rounded-lg ">
                      <Image
                        src={"/facebook_icon.png"}
                        alt="Ikona Facebook"
                        width={17}
                        height={17}
                        className=""
                      />
                      <div
                        onClick={handleFacebookShare}
                        className="text-[16px] ml-[10px] cursor-pointer text-[#00B4D8] font-variation-customOpt16 font-normal"
                      >
                        Deli na Facebooku
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="flex-col
                            mt-[24px]
                            mobile:mt-[28px]
                            py-4      
                            pl-[21px] pr-[28px]
                            w-[100%] mobile:!w-[100%] mobile:!max-w-[470px] tablet:w-[550px] tablet:px-4 
                            desktop:w-[517px] sm:w-[517px] desktop:pl-[22px] desktop:pr-[17px]
                             shadow-custom-light-dark-box bg-gradient-to-br rounded-2xl from-[#E3E8EC] to-[#FFFFFF]"
                  style={{
                    background:
                      "linear-gradient(113.63deg, #E3E8EC 0%, #FFFFFF 100%)",
                    boxShadow:
                      "-5px -5px 10px 0px #FFFFFF, 5px 5px 10px 0px #C2C2C280",
                  }}
                  // onClick={()=>toast.success("Kmalu")}
                  onClick={handleQr}
                >
                  <div className="text-[20px] text-[#1E2125] font-variation-customOpt20 font-normal  w-full">
                    QR koda do te strani
                  </div>
                  <div className="flex items-end justify-end gap-[10px]">
                    <span className="text-[16px] text-[#414141] font-variation-customOpt12 font-normal">
                      Klikni za povečavo
                    </span>
                    <Image
                      src={"/qr_demo.png"}
                      alt="Primer QR kode"
                      width={72}
                      height={72}
                      className=""
                    />
                  </div>
                </div>

                <div className="flex items-end flex-col-reverse self-end mt-4 tablet:mt-6 desktop:pr-[20px] desktop:mt-6 gap-3 tablet:gap-4 desktop:gap-4">
                  <div
                    onClick={() => {
                      // set_Id("error_report"), openCandleModal();
                      router.push("/kontakt");
                    }}
                    className="flex cursor-pointer h-[15px] desktop:h-[16px] items-center"
                  >
                    <Image
                      src={"/flag.png"}
                      alt="Ikona zastave"
                      width={13}
                      height={13}
                      className=""
                    />
                    <p className="text-[12px] text-[#414141] font-variation-customOpt12 font-normal ml-[10px] ">
                      Sporoči napake
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full pb-10">
        <div className="flex flex-col w-full items-center mt-[40px] tablet:mt-[45px] desktop:mt-[60px] ">
          <PremiumCandles
            obituaryId={data?.id}
            onLoaded={setHasPremiumCandle}
            // Premium candles are a separate product: this opens the order popup, nothing of the free candle.
            onOrderCandle={onOrder ? openPremiumCandleOrder : undefined}
          />

          <div className="flex flex-col w-full items-center mt-[40px] tablet:mt-[60px] desktop:mt-[70px] ">
            <div className="flex items-center justify-center h-[33px] tablet:h-[47px] desktop:h-[47px] relative">
              <h2 className="text-[#1E2125] text-[28px] tablet:text-[40px] desktop:text-[40px] font-variation-customOpt28 tablet:font-variation-customOpt40 desktop:font-variation-customOpt40 font-normal">
                Žalna knjiga
              </h2>
              {/* <div className="text-[#0A85C2] text-[24px] font-[400] absolute top-[-3px] right-[-38px] text-end">
                22
              </div> */}
            </div>
            <div
              className="flex items-center mt-4 h-6 cursor-pointer"
              onClick={() => {
                set_Id("20");
                openCandleModal();
              }}
            >
              <p className="text-[16px] text-[#414141] font-variation-customOpt16 font-normal">
                Odpri knjigo
              </p>
            </div>
          </div>
          <div className="flex flex-col w-full items-center mt-[48px] ">
            <UserCircles
              onTextClick={() => {
                set_Id("17");
                openCandleModal();
              }}
              onCircle={() => {
                set_Id("20");
                openCandleModal();
              }}
              users={data?.SorrowBooks}
            />
          </div>
          {onOrder && hasPremiumCandle === false && (
            <div
              className="ml-auto mr-[14px] mb-5 desktop:mr-[18%] flex items-center cursor-pointer "
              onClick={openPremiumCandleOrder}
            >
              <Image
                src={"/round_add.png"}
                alt="Ikona dodajanja"
                width={100}
                height={100}
                className="w-[12px] mb-[2px] h-[12px] mr-[10px]"
              />
              <p className="text-[14px] text-[#414141] font-variation-customOpt12 font-normal">
                Dodaj spominsko svečko
              </p>
            </div>
          )}
          {combinedDedicationSlides.length > 0 ? null : (
            <div
              className="ml-auto mr-[14px] mb-5 desktop:mr-[18%] flex items-center cursor-pointer "
              onClick={() => {
                openSpomin();
              }}
            >
              <Image
                src={"/round_add.png"}
                alt="Ikona dodajanja"
                width={100}
                height={100}
                className="w-[12px] mb-[2px] h-[12px] mr-[10px]"
              />
              <p className="text-[14px] text-[#414141] font-variation-customOpt12 font-normal">
                Dodaj posvetilo
              </p>
            </div>
          )}
          {data?.Photos && data?.Photos.length > 0 ? null : (
            <div
              className="ml-auto mr-[14px] desktop:mr-[18%] flex items-center cursor-pointer "
              onClick={() => {
                set_Id("6");
                openCandleModal();
              }}
            >
              <Image
                src={"/round_add.png"}
                alt="Ikona dodajanja"
                width={100}
                height={100}
                className="w-[12px] mb-[2px] h-[12px] mr-[10px]"
              />
              <p className="text-[14px]  text-[#414141] font-variation-customOpt12 font-normal">
                Dodaj fotografije
              </p>
            </div>
          )}
        </div>
      </div>
      {combinedDedicationSlides.length > 0 && (
        <div className="w-full pb-[150px]">
          <div className="flex flex-col w-full items-center">
            <div className="flex flex-col w-full items-center mt-[100px] ">
              <div className="flex items-center justify-center h-[33px] tablet:h-[47px] desktop:h-[47px] relative mobile:mb-[16px]">
                <h2 className="text-[#1E2125] text-[28px] tablet:text-[40px] desktop:text-[40px] font-variation-customOpt28 tablet:font-variation-customOpt40 desktop:font-variation-customOpt40 font-normal">
                  Posvetilo
                </h2>
                {/* <div className="text-[#0A85C2] text-[24px] font-[400] absolute top-[-3px] right-[-38px]">
                  22
                </div> */}
              </div>
              <div
                className="flex items-center mt-4 h-6 cursor-pointer"
                onClick={() => {
                  openSpomin();
                }}
              >
                <p className="text-[16px] text-[#414141] font-variation-customOpt16 font-normal text-center mobile:w-[306px] mobile:mx-auto">
                  Delite zgodbe, čarobne trenutke, morda biografijo, zadnji
                  pozdrav
                </p>
              </div>
            </div>

            <div
              className="mt-[30px] w-[720px] mobile:w-[calc(100%-20px)] mobile:max-w-[500px] mx-auto flex items-center justify-end cursor-pointer mb-[18px] px-[10px]"
              onClick={() => {
                openSpomin();
              }}
            >
              <Image
                src={"/round_add.png"}
                alt="Ikona dodajanja"
                width={100}
                height={100}
                className="w-[12px] mb-[2px] h-[12px] mr-[10px]"
              />
              <p className="text-[14px] text-[#414141] font-variation-customOpt12 font-normal">
                Dodaj posvetilo
              </p>
            </div>

            <ContentSlider data={combinedDedicationSlides} />
          </div>
        </div>
      )}
      {data?.Photos && data?.Photos?.length > 0 && (
        <div className="w-full pb-[150px]">
          <div className="flex flex-col w-full items-center">
            <div className="flex items-center justify-center h-[33px] tablet:h-[47px] desktop:h-[47px] relative">
              <h2 className="text-[#1E2125] text-[28px] tablet:text-[40px] desktop:text-[40px] font-variation-customOpt28 tablet:font-variation-customOpt40 desktop:font-variation-customOpt40 font-normal">
                Nepozabni trenutki
              </h2>
              {/* <div className="text-[#0A85C2] text-[24px] font-[400] absolute top-[-3px] right-[-38px]">
                22
              </div> */}
            </div>
            <button
              className="flex cursor-pointer self-center tablet:self-start desktop:self-start items-center justify-center flex-col gap-[2px] border-2 rounded-[4px] border-[#FFFFFF] w-[165px] h-[60px] bg-gradient-to-br from-[#FFFFFF] to-[#FFFFFF30] z-20 mx-auto mt-[40px] tablet:mt-[40px] desktop:mt-[40px] tablet:hidden mobile:hidden mb-[40px]"
              style={{
                boxShadow: "3px 3px 18px 0px #00000040",
              }}
              onClick={() => {
                set_Id("6");
                openCandleModal();
              }}
            >
              <Image
                src={"/memory_page_plus_icon.png"}
                alt="Ikona dodajanja vsebine"
                width={20}
                height={20}
                className=""
              />
              <p className="text-[16px] text-[#1E2125] font-variation-customOpt16 font-normal">
                Dodaj sliko
              </p>
            </button>

            <div className="mt-[40px] tablet:mt-[40px] desktop:mt-[40px] w-full">
              <Gallery3D photos={data?.Photos || []} onPhotoClick={setLightboxIndex} />
            </div>
            {lightboxIndex !== null && (
              <PhotoLightbox
                photos={data?.Photos || []}
                index={lightboxIndex}
                onChange={setLightboxIndex}
                onClose={() => setLightboxIndex(null)}
              />
            )}

            <button
              onClick={() => {
                set_Id("6");
                openCandleModal();
              }}
              className="flex gap-[8px] items-center justify-end w-[1024px] tablet:w-[610px] mobile:w-[321px] text-end desktop:hidden text-[#414141] text-[14px] font-[400] mt-[40px] mb-[12px]"
            >
              <Image
                src={"/memory_page_plus_icon.png"}
                alt="Ozadje spominske strani"
                width={16}
                height={16}
                className=""
              />
              Dodaj Sliko
            </button>
          </div>
        </div>
      )}

      <div className="w-[1024px] tablet:w-[678.78px]   mobile:w-[341px] h-auto  flex flex-col items-center ">
        <div className="flex flex-row h-[47px] items-center relative">
          <h2 className="font-variation-customOpt40 text-[40px] leading-[46.88px] mr-[8px] mobile:text-[28px] mobile:leading-[32.9px] mobile:font-variation-customOpt28 text-[#1E2125] ">
            Sožalja
          </h2>
          {/* <div className="text-[#0A85C2] text-[24px] font-[400] absolute top-[-3px] right-[-29px]">
            22
          </div> */}
        </div>

        <button
          className="flex cursor-pointer self-center tablet:self-start desktop:self-start items-center justify-center flex-col gap-[2px] border-2 rounded-[4px] border-[#FFFFFF] w-[165px] h-[60px] bg-gradient-to-br from-[#FFFFFF] to-[#FFFFFF30] z-20 mx-auto mt-[47px] mb-[48px]"
          style={{
            boxShadow: "3px 3px 18px 0px #00000040",
          }}
          onClick={() => {
            set_Id("sayings_condolence");
            openCandleModal();
          }}
        >
          <Image
            src={"/memory_page_plus_icon.png"}
            alt="Ozadje spominske strani"
            width={20}
            height={20}
            className=""
          />
          <p className="text-[16px] text-[#1E2125] font-variation-customOpt16 font-normal">
            Dodaj Sožalje
          </p>
        </button>

        <div className="w-[824px] tab:w-[629px] desktop:w-[824px] mobile:w-[321px] grid grid-cols-2 mobile:grid-cols-1 mx-auto  gap-[24px]">
          <div className="flex flex-col gap-[27px]">
            {limitedCondolances?.length > 0 ? (
              limitedCondolances
                .filter((_, index) => index % 2 === 0)
                .map((item, index) => (
                  <div
                    className="bg-white rounded-[3px] text-[16px] text-[#414141]"
                    style={{
                      boxShadow: "5px 5px 10px 0px #C2C2C280",
                    }}
                    key={index}
                  >
                    <div className="px-[17px] pt-[14px] pb-[28px]">
                      {item.message}
                    </div>
                    <div className="bg-[#E1E7E8] text-[#6D778E] px-[16px] py-[4px] flex justify-between">
                      <div className="flex items-end gap-[4px]">
                        <span className="text-[16px] leading-none">
                          {item.name}
                        </span>
                        <span className="text-[12px] leading-none">
                          {item.relation}
                        </span>
                      </div>
                      <span className="text-[12px] leading-none">
                        {formatDate(item?.createdTimestamp)}
                      </span>
                    </div>
                  </div>
                ))
            ) : (
              <div
                className="bg-white rounded-[3px] text-[16px] text-[#414141]"
                style={{
                  boxShadow: "5px 5px 10px 0px #C2C2C280",
                }}
              >
                <div className="px-[17px] pt-[14px] pb-[28px]">
                  {defaultMessage.message}
                </div>
                <div className="bg-[#E1E7E8] text-[#6D778E] px-[16px] py-[4px] flex justify-between">
                  <div className="flex items-end gap-[4px]">
                    <span className="text-[16px] leading-none">
                      {defaultMessage.name}
                    </span>
                    <span className="text-[12px] leading-none">
                      {defaultMessage.relation}
                    </span>
                  </div>
                  <span className="text-[12px] leading-none">
                    {formatDate(defaultMessage?.createdTimestamp)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-[27px]">
            {limitedCondolances &&
              limitedCondolances
                .filter((_, index) => index % 2 !== 0)
                .map((item, index) => (
                  <div
                    className="bg-white rounded-[3px] text-[16px] text-[#414141]"
                    style={{
                      boxShadow: "5px 5px 10px 0px #C2C2C280",
                    }}
                    key={index}
                  >
                    <div className="px-[17px] pt-[14px] pb-[28px]">
                      {item.message}
                    </div>
                    <div className="bg-[#E1E7E8] text-[#6D778E] px-[16px] py-[4px] flex justify-between">
                      <div className="flex items-end gap-[4px]">
                        <span className="text-[16px] leading-none">
                          {item.name}
                        </span>
                        <span className="text-[12px] leading-none">
                          {item.relation}
                        </span>
                      </div>
                      <span className="text-[12px] leading-none">
                        {formatDate(item?.createdTimestamp)}
                      </span>
                    </div>
                  </div>
                ))}
          </div>
        </div>
        <div
          className="mt-[24px] mb-[71px] mx-auto"
          onClick={() => {
            setMaxCondolances((prev) => prev + 10);
          }}
        >
          <Image
            src={"/memory_down.png"}
            alt="Puščica navzdol"
            width={74}
            height={74}
          // className="mt-[24px] mb-[71px] mx-auto"
          />
        </div>
      </div>

      <Modal
        index={1}
        open={isPopUP1}
        onClose={() => setIsPopUP1(false)}
        onFooterClick={() => {
          setIsPopUP1(false);
          setIsPopUP2(true);
        }}
      />
      <Modal
        index={2}
        open={isPopUP2}
        onClose={() => setIsPopUP2(false)}
        onFooterClick={() => {
          router.push("/spominska");
        }}
      />
    </div>
  );
};

const UserCircles = ({ onTextClick, onCircle, users }) => {
  return (
    <div className="flex flex-col tablet:flex-row desktop:flex-row ">
      <button
        onClick={onTextClick}
        className="flex cursor-pointer self-center tablet:self-start desktop:self-start items-center justify-center flex-col gap-[2px] border-2 rounded-[100px] border-[#FFFFFF] w-[165px] h-[60px] bg-gradient-to-br from-[#FFFFFF] to-[#FFFFFF30] z-20"
      >
        <Image
          src={"/memory_page_plus_icon.png"}
          alt="Ozadje spominske strani"
          width={20}
          height={20}
          className=""
        />
        <p className="text-[16px] text-[#1E2125] font-variation-customOpt16 font-normal">
          Dodaj svoje ime
        </p>
      </button>
      <div className="hidden   tablet:flex desktop:flex  flex-row">
        {[...(users || [])] .reverse() .slice(0, 7) .map((item, index) => (
            <div key={index}>
              <Container item={item} index={index} onCircleClick={onCircle} />
            </div>
          ))}
        {users?.length > 7 && (
          <button className="flex border-2 backdrop-blur-lg cursor-pointer bg-gradient-to-br from-[#FFFFFF80] to-[#FFFFFF30] border-[#FFFFFF] items-center justify-center w-[64px] h-[64px] rounded-full ml-[-10px]">
            <div className="text-[20px] text-[#1E2125] font-normal">
              +{users.length - 7}
            </div>
          </button>
        )}
      </div>
      <div className="flex tablet:hidden desktop:hidden justify-center mt-[48px] flex-row">
        {[...(users || [])] .reverse() .slice(0, 4) .map((item, index) => (
            <div key={index}>
              <Container item={item} index={index} onCircleClick={onCircle} />
            </div>
          ))}

        {users?.length > 4 && (
          <button className="flex border-2 backdrop-blur-lg cursor-pointer bg-gradient-to-br from-[#FFFFFF80] to-[#FFFFFF30] border-[#FFFFFF] items-center justify-center w-[64px] h-[64px] rounded-full ml-[-10px]">
            <div className="text-[20px] text-[#1E2125] font-normal">
              +{users.length - 4}
            </div>
          </button>
        )}
      </div>
    </div>
  );
};
// Deterministic hash function (simple but works for this use case)
const getConsistentIndex = (input, length) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % length;
};

// Use this for gradients
const getConsistentGradientClass = (input) => {
  const index = getConsistentIndex(input, gradientStyles.length);
  return gradientStyles[index].class;
};

const Container = ({ index, item, onCircleClick }) => {
  const userIdentifier = item?.createdTimestamp || "default";
  const gradientClass = useMemo(
    () => getConsistentGradientClass(userIdentifier),
    [userIdentifier]
  );

  return (
    <button
      onClick={onCircleClick}
      key={index}
      className={`flex border-2 backdrop-blur-lg cursor-pointer ${gradientClass}  items-center justify-center w-[64px] h-[64px] rounded-full ml-[-10px]`}
    >
      <div className="text-[20px] text-[#1E2125] font-normal">
        {(() => {
          const nameParts = (item?.name || "").trim().split(/\s+/).filter(Boolean);
          const initials =
            nameParts.length > 1
              ? nameParts[0][0] + nameParts[1][0]
              : nameParts[0]?.substring(0, 1) || "";

          return initials.toUpperCase();
        })()}
      </div>
    </button>
  );
};
const gradientStyles = [
  {
    class: "bg-gradient-to-br from-[#F1A5F380] to-[#FFFFFF30] border-[#F1A5F3]",
  },

  {
    class: "bg-gradient-to-br from-[#A6A5F380] to-[#FFFFFF30] border-[#A6A5F3]",
  },
  {
    class: "bg-gradient-to-br from-[#ABEDED80] to-[#FFFFFF30] border-[#ABEDED]",
  },
  {
    class: "bg-gradient-to-br from-[#F3ACA580] to-[#FFFFFF30] border-[#F3ACA5]",
  },
  {
    class: "bg-gradient-to-br from-[#B9D1DF80] to-[#FFFFFF30] border-[#B9D1DF]",
  },
  {
    class: "bg-gradient-to-br from-[#B2E6E380] to-[#FFFFFF30] border-[#B2E6E3]",
  },
];

const ContentSlider = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? data?.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === data?.length - 1 ? 0 : prev + 1));
  };
  const currentDedication = data[activeIndex];
  const many = (data?.length ?? 0) > 1;

  // With more than one message they change by themselves every 20 seconds (a click on an arrow starts the count again).
  useEffect(() => {
    if (!many) return undefined;
    const timer = setTimeout(handleNext, 20000);
    return () => clearTimeout(timer);
  }, [activeIndex, many]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("sl-SI", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };
  const [showFullDedicationText, setShowFullDedicationText] = useState(false);
    useEffect(() => { setShowFullDedicationText(false); }, [activeIndex]);
  const toggleDedicationText = () => {
    setShowFullDedicationText((prev) => !prev);
  };

  const characterLimit = 300;
  const message = currentDedication?.message || "";
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const messageRef = useRef(null);
  const maxHeight = 198;

  useEffect(() => {
    if (messageRef.current && message.length > characterLimit) {
      const height = messageRef.current.scrollHeight;
      if (height > maxHeight) {
        setShouldTruncate(true);
      } else {
        setShouldTruncate(false); // reset if height is OK
      }
    } else {
      setShouldTruncate(false); // reset if under char limit
    }
  }, [currentDedication]);

  return (
    <div className="flex h-auto object-cover w-full items-center mt-[10px] gap-[38px] justify-center relative px-[10px]">
      {many && (
        <Image
          onClick={handlePrev}
          src={"/memory_slider_left.png"}
          alt="Prejšnje sporočilo"
          width={74}
          height={74}
          className="cursor-pointer tablet:absolute tablet:z-10 tablet:left-0 tablet:top-1/2 tablet:-translate-y-1/2 mobile:absolute mobile:z-10 mobile:left-[2px] mobile:top-1/2 mobile:h-[40px] mobile:w-[40px] mobile:-translate-y-1/2 mobile:opacity-80"
        />
      )}
      {currentDedication?.isThankYou ? (
        // A paid Zahvala / Spomin: our own box (the same as on the Zahvala page), without the obituary card.
        <div className="w-[720px] mobile:w-full mobile:max-w-[500px]">
          <ThankYouMessageCard message={currentDedication} inCarousel />
        </div>
      ) : (
      <div className="flex flex-col gap-[10px] w-[720px] mobile:w-full mobile:max-w-[500px] min-h-[370px] h-auto mobile:h-auto overflow-hidden rounded-[3px] relative pt-[38px] mobile:py-[40px] mobile:px-[35px]">
        <Image
          src={"/memory_paper_slider.jpg"}
          alt="Background"
          width={720}
          height={370}
          className="absolute top-0 left-0 object-cover w-full h-full"
        />
        <div className="w-[522px] h-auto mobile:w-full mobile:max-w-[330px] relative mx-auto">
          <div className="flex justify-between items-center gap-[10px]">
            <div
              className="text-[40px] mobile:text-[24px] font-greatVibes font-normal text-[#1E2125]"
              style={{
                textShadow: "0px 1px 1px #000000, 0px 4px 4px #00000040",
              }}
            >
              {currentDedication?.title}
            </div>
            <div className="flex flex-col items-end justify-end gap-[7px] ">
              <p className="text-[14px] leading-[14px] font-variation-customOpt16 font-normal text-[#36556CE5]">
                {currentDedication?.name}
              </p>
              <p className="text-[12px] leading-[12px] font-variation-customOpt16 font-normal text-[#36556CE5]">
                {formatDate(currentDedication?.createdTimestamp)}
              </p>
            </div>
          </div>
          <p
            ref={messageRef}
            className="text-[16px] leading-[24px] font-normal text-[#1E2125] mt-[19px]"
            style={{
              background:
                "linear-gradient(180deg, #414141 76.56%, rgba(166, 166, 167, 0.1) 96.35%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              maxHeight:
                !showFullDedicationText && shouldTruncate
                  ? `${maxHeight}px`
                  : "none",
              overflow: "hidden",
            }}
          >
            {message.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </p>

          {shouldTruncate && !showFullDedicationText && (
            <p
              onClick={toggleDedicationText}
              className="text-end text-[12px] leading-[100%] font-regular text-[#36556CB2]  mt-[26px]"
            >
              Odpri več
            </p>
          )}

          {shouldTruncate && showFullDedicationText && (
            <p
              onClick={toggleDedicationText}
              className="text-end text-[12px] leading-[100%] font-regular text-[#36556CB2]  mt-[26px]"
            >
              Zapri
            </p>
          )}
        </div>
      </div>
      )}
      {many && (
        <Image
          onClick={handleNext}
          src={"/memory_slider_right.png"}
          alt="Naslednje sporočilo"
          width={74}
          height={74}
          className="cursor-pointer tablet:absolute tablet:z-10 tablet:right-0 tablet:top-1/2 tablet:-translate-y-1/2 mobile:absolute mobile:z-10 mobile:right-[2px] mobile:top-1/2 mobile:h-[40px] mobile:w-[40px] mobile:-translate-y-1/2 mobile:opacity-80"
        />
      )}
    </div>
  );
};

export default MemorialPageTopComp;
